import { useState, useEffect } from 'react';

// Định nghĩa các kích thước màn hình cho các thiết bị mobile phổ biến
export type DeviceType = 
  | 'iphoneSE' 
  | 'iphoneXR' 
  | 'iphone12' 
  | 'iphone14ProMax' 
  | 'samsungGalaxy' 
  | 'tablet' 
  | 'desktop';

interface Breakpoints {
  iphoneSE: number;      // 375px
  iphoneXR: number;      // 414px
  iphone12: number;      // 390px
  iphone14ProMax: number; // 428px
  samsungGalaxy: number; // 360px
  tablet: number;        // 768px
  desktop: number;       // 1024px
}

// Kích thước màn hình tối đa cho mỗi thiết bị (chiều rộng tính bằng pixel)
const breakpoints: Breakpoints = {
  iphoneSE: 375,
  iphoneXR: 414,
  iphone12: 390,
  iphone14ProMax: 428,
  samsungGalaxy: 360,
  tablet: 768,
  desktop: 1024
};

interface ResponsiveReturn {
  width: number;
  height: number;
  device: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLandscape: boolean;
  isPortrait: boolean;
  // Helpers cho từng loại thiết bị cụ thể
  isIphoneSE: boolean;
  isIphoneXR: boolean;
  isIphone12: boolean;
  isIphone14ProMax: boolean;
  isSamsungGalaxy: boolean;
}

/**
 * Custom hook để quản lý responsive cho các thiết bị khác nhau
 * @returns Thông tin về kích thước màn hình và loại thiết bị
 */
export function useResponsive(): ResponsiveReturn {
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [height, setHeight] = useState<number>(window.innerHeight);
  
  useEffect(() => {
    // Hàm xử lý khi kích thước màn hình thay đổi
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };
    
    // Đăng ký event listener
    window.addEventListener('resize', handleResize);
    
    // Cleanup event listener khi component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Xác định loại thiết bị dựa trên kích thước màn hình
  const getDeviceType = (): DeviceType => {
    if (width <= breakpoints.samsungGalaxy) return 'samsungGalaxy';
    if (width <= breakpoints.iphoneSE) return 'iphoneSE';
    if (width <= breakpoints.iphone12) return 'iphone12';
    if (width <= breakpoints.iphoneXR) return 'iphoneXR';
    if (width <= breakpoints.iphone14ProMax) return 'iphone14ProMax';
    if (width <= breakpoints.tablet) return 'tablet';
    return 'desktop';
  };
  
  const device = getDeviceType();
  const isLandscape = width > height;
  const isPortrait = !isLandscape;
  
  return {
    width,
    height,
    device,
    isMobile: width < breakpoints.tablet,
    isTablet: width >= breakpoints.tablet && width < breakpoints.desktop,
    isDesktop: width >= breakpoints.desktop,
    isLandscape,
    isPortrait,
    isIphoneSE: device === 'iphoneSE',
    isIphoneXR: device === 'iphoneXR',
    isIphone12: device === 'iphone12',
    isIphone14ProMax: device === 'iphone14ProMax',
    isSamsungGalaxy: device === 'samsungGalaxy'
  };
}

// Hàm tiện ích để tạo các styles responsive dựa trên thiết bị
export function getResponsiveStyles(deviceType: DeviceType) {
  // Các giá trị padding, margin, font-size phù hợp cho từng thiết bị
  const styles = {
    iphoneSE: {
      padding: '12px',
      fontSize: {
        h1: '24px',
        h2: '20px',
        body: '14px',
        small: '12px'
      },
      spacing: {
        xs: '4px',
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '24px'
      }
    },
    samsungGalaxy: {
      padding: '12px',
      fontSize: {
        h1: '24px',
        h2: '20px',
        body: '14px',
        small: '12px'
      },
      spacing: {
        xs: '4px',
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '24px'
      }
    },
    iphone12: {
      padding: '16px',
      fontSize: {
        h1: '26px',
        h2: '22px',
        body: '15px',
        small: '13px'
      },
      spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '20px',
        xl: '28px'
      }
    },
    iphoneXR: {
      padding: '16px',
      fontSize: {
        h1: '28px',
        h2: '24px',
        body: '16px',
        small: '14px'
      },
      spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px'
      }
    },
    iphone14ProMax: {
      padding: '20px',
      fontSize: {
        h1: '30px',
        h2: '26px',
        body: '16px',
        small: '14px'
      },
      spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px'
      }
    },
    tablet: {
      padding: '24px',
      fontSize: {
        h1: '32px',
        h2: '28px',
        body: '18px',
        small: '16px'
      },
      spacing: {
        xs: '8px',
        sm: '12px',
        md: '20px',
        lg: '28px',
        xl: '36px'
      }
    },
    desktop: {
      padding: '32px',
      fontSize: {
        h1: '48px',
        h2: '32px',
        body: '18px',
        small: '16px'
      },
      spacing: {
        xs: '8px',
        sm: '16px',
        md: '24px',
        lg: '32px',
        xl: '48px'
      }
    }
  };
  
  return styles[deviceType];
}
